import API_BASE from './api.js';

const ALLOWED_ATTACHMENT_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
  'image/svg+xml',
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

const MIME_BY_EXTENSION = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  avif: 'image/avif',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  txt: 'text/plain',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

function inferMimeFromFilename(filename) {
  const name = String(filename || '').toLowerCase();
  const parts = name.split('.');
  if (parts.length < 2) return '';
  return MIME_BY_EXTENSION[parts.pop()] || '';
}

function createClientId() {
  return `attachment-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function parseJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || 'Request failed');
  }
  return data;
}

export function isImageAttachment(attachment) {
  return /^image\//.test(attachment?.type || '');
}

export async function uploadChatAttachments(files) {
  const fileList = Array.from(files || []);
  const uploads = [];
  const token = localStorage.getItem('idToken');

  for (const file of fileList) {
    const mimeFromFile = String(file.type || '').toLowerCase();
    const mime = ALLOWED_ATTACHMENT_MIME_TYPES.has(mimeFromFile)
      ? mimeFromFile
      : inferMimeFromFilename(file.name);

    if (!ALLOWED_ATTACHMENT_MIME_TYPES.has(mime)) {
      throw new Error(`Unsupported file type: ${file.name}`);
    }

    const signRes = await fetch(
      `${API_BASE}/api/upload/presigned-url?filename=${encodeURIComponent(
        file.name
      )}&type=${encodeURIComponent(mime)}&size=${encodeURIComponent(file.size)}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
    );
    const sign = await parseJson(signRes);
    const uploadUrl = sign.uploadUrl || sign.url || sign.presignedUrl;
    const fileUrl = sign.fileUrl || sign.publicUrl;

    if (!uploadUrl || !fileUrl) {
      throw new Error('Invalid upload response');
    }

    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': mime },
      body: file,
    });

    if (!putRes.ok) {
      throw new Error(`Failed to upload ${file.name}`);
    }

    uploads.push({
      id: createClientId(),
      name: file.name,
      url: fileUrl,
      type: mime,
      size: file.size,
    });
  }

  return uploads;
}

export async function fetchProjectMessages(userId, projectId) {
  const res = await fetch(
    `${API_BASE}/api/project-chat/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}/messages`,
    { credentials: 'include' }
  );
  return parseJson(res);
}

export async function sendProjectMessage(userId, projectId, payload) {
  const res = await fetch(
    `${API_BASE}/api/project-chat/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}/messages`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return parseJson(res);
}
